import { observer } from 'mobx-react';
import { useOnboardingStore } from '~contexts/mobx';
import { Section } from '~components/form-fields/section';
import { KpiCard } from '~analytics-components/kpi-card';
import type { KpiTileData } from '~analytics-components/kpi-tile';
import style from './style.module.scss';
import { getAppDownloadInfoLines } from './helpers';

/**
 * One installs tile. A figure the API could not serve shows a placeholder
 * instead of a number (the total does whenever a platform is missing, since a
 * one-sided sum would read as the whole), and carries no period delta: there is
 * nothing honest to compare it against. The reason is on the card, in the info
 * lines.
 */
const downloadTile = (
    title: string,
    value: number | null | undefined,
    previousValue: number | null | undefined,
    isComparing: boolean
): KpiTileData =>
    value == null
        ? { title, value: 'N/A' }
        : {
              title,
              value,
              previousValue:
                  isComparing && previousValue != null
                      ? previousValue
                      : undefined,
          };

export const AcquisitionFunnelSection = observer(
    ({ isComparing }: { isComparing: boolean }) => {
        const onboardingStore = useOnboardingStore();

        const appData = onboardingStore.appDownloadsData;
        const appDataPrevious = onboardingStore.appDownloadsDataPrevious;
        const appDownloadInfoLines = getAppDownloadInfoLines(appData);

        const completedValue =
            onboardingStore.progressionData.find(
                (p) => p.status === 'COMPLETED'
            )?.total || 0;
        const inProgressValue =
            onboardingStore.progressionData.find(
                (p) => p.status === 'IN_PROGRESS'
            )?.total || 0;
        const startedOnboarding = inProgressValue + completedValue;

        const accountSetupCount =
            onboardingStore.onboardingData.find(
                (s) => s.onboardingStepName === 'account_setup'
            )?.completedCount || 0;
        const congratulationsCount =
            onboardingStore.onboardingData.find(
                (s) => s.onboardingStepName === 'congratulations_setup'
            )?.completedCount || 0;
        const dropOffValue = Math.max(
            0,
            accountSetupCount - congratulationsCount
        );

        const completedPreviousValue =
            onboardingStore.progressionDataPrevious.find(
                (p) => p.status === 'COMPLETED'
            )?.total || 0;
        const inProgressPreviousValue =
            onboardingStore.progressionDataPrevious.find(
                (p) => p.status === 'IN_PROGRESS'
            )?.total || 0;
        const startedOnboardingPrevious =
            inProgressPreviousValue + completedPreviousValue;

        const accountSetupPreviousCount =
            onboardingStore.onboardingDataPrevious.find(
                (s) => s.onboardingStepName === 'account_setup'
            )?.completedCount || 0;
        const congratulationsPreviousCount =
            onboardingStore.onboardingDataPrevious.find(
                (s) => s.onboardingStepName === 'congratulations_setup'
            )?.completedCount || 0;
        const dropOffPreviousValue = Math.max(
            0,
            accountSetupPreviousCount - congratulationsPreviousCount
        );

        const appDownloadKpis = [
            downloadTile(
                'Total downloads',
                appData?.total,
                appDataPrevious?.total,
                isComparing
            ),
            downloadTile(
                'iOS',
                appData?.ios,
                appDataPrevious?.ios,
                isComparing
            ),
            downloadTile(
                'Android',
                appData?.android,
                appDataPrevious?.android,
                isComparing
            ),
        ];

        const onboardingKpis = [
            {
                title: 'Started onboarding',
                value: startedOnboarding,
                previousValue: isComparing
                    ? startedOnboardingPrevious
                    : undefined,
            },
            {
                title: 'Completed onboarding',
                value: completedValue,
                previousValue: isComparing ? completedPreviousValue : undefined,
            },
        ];

        const profileKpis = [
            {
                title: 'Completed profiles (current)',
                value: completedValue,
                previousValue: isComparing ? completedPreviousValue : undefined,
            },
            {
                title: 'Drop off',
                value: dropOffValue,
                previousValue: isComparing ? dropOffPreviousValue : undefined,
            },
        ];

        return (
            <Section
                className={style.section_container}
                title="Acquisition funnel"
                noCard
            >
                <div className={style.kpi_tiles_grid}>
                    <KpiCard
                        title="Application download"
                        isLoading={onboardingStore.isFetchingAppDownloads}
                        kpis={appDownloadKpis}
                        infoLines={appDownloadInfoLines}
                    />
                    <KpiCard
                        title="Onboarding"
                        isLoading={onboardingStore.isFetchingProgressionData}
                        kpis={onboardingKpis}
                        layout="stacked"
                    />
                    <KpiCard
                        title="Profile activity"
                        isLoading={
                            onboardingStore.isFetchingProgressionData ||
                            onboardingStore.isFetchingOnboardingData
                        }
                        kpis={profileKpis}
                        layout="stacked"
                    />
                </div>
            </Section>
        );
    }
);
